/**
 * SB6-AUTH-RECONNECT-01 — activity tracker.
 *
 * Listens for `mousemove`, `keydown`, and `click` on `document` and
 * writes `lastActivityAt = Date.now()` into the auth store. Throttled
 * to one write per `ACTIVITY_THROTTLE_MS` (30 s) to avoid Zustand thrash
 * — `mousemove` alone can fire dozens of times per second.
 *
 * The store value is read by `handleRefreshFailure()` in
 * fetchWithRefresh.ts to decide between:
 *   - active user → clear session, bounce to /login (rare; usually a
 *     network blip)
 *   - idle user (>= 8 h) → soft modal-over-current-page; tokens stay in
 *     store so the user resumes in place after re-auth
 *
 * Mount once at the app shell. Listeners are `passive` so they don't
 * block scrolling.
 */

import { useEffect } from "react";
import { useAuthStore } from "./store.js";
import { ACTIVITY_THROTTLE_MS } from "./sessionLimits.js";

const ACTIVITY_EVENTS: readonly (keyof DocumentEventMap)[] = [
  "mousemove",
  "keydown",
  "click",
] as const;

interface InstallOptions {
  /** Inject the time source for tests. Defaults to `Date.now`. */
  now?: () => number;
  /** Inject the document for tests. Defaults to the global `document`. */
  doc?: Document;
}

/**
 * Imperative installer. Returns a cleanup function. Exposed for direct
 * use from non-React code AND for unit tests.
 */
export function installActivityTracker(opts: InstallOptions = {}): () => void {
  const now = opts.now ?? Date.now;
  const doc =
    opts.doc ?? (typeof document !== "undefined" ? document : undefined);
  if (!doc) {
    // SSR / non-DOM context — return a no-op cleanup the caller can
    // still call without a guard.
    return (): void => {
      /* no document; nothing to tear down */
    };
  }

  let lastWrite = 0;

  const handler = (): void => {
    const ts = now();
    if (ts - lastWrite < ACTIVITY_THROTTLE_MS) return;
    lastWrite = ts;
    useAuthStore.getState().setLastActivityAt(ts);
  };

  for (const ev of ACTIVITY_EVENTS) {
    doc.addEventListener(ev, handler, { passive: true });
  }

  return () => {
    for (const ev of ACTIVITY_EVENTS) {
      doc.removeEventListener(ev, handler);
    }
  };
}

/**
 * React-flavoured installer. Mount once at the app shell:
 *   `useActivityTracker();`
 */
export function useActivityTracker(): void {
  useEffect(() => {
    return installActivityTracker();
  }, []);
}
